import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Users, X, MessageCircle, AlertCircle, PenSquare, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { RichTextEditor } from "@/components/forms/RichTextEditor";
import { ChatterComposerMedia, EMPTY_MEDIA, hasMedia } from "@/components/social/PostMedia";
import { LinkPreviewCard, useLinkPreview } from "@/components/social/LinkPreviewCard";
import { MultiSelect } from "@/components/forms/MultiSelect";
import { PostCard, LabelSelectors, identityKeyOf, LAST_IDENTITY_KEY } from "@/components/social/ChatterFeed";
import { OtterAssistComposerPanel, useOtterAssist } from "@/components/social/OtterAssistComposerPanel";
import { OtterAssistActivationDialog } from "@/components/social/OtterAssistActivationDialog";
import { ChatterProfileCard } from "@/components/social/ChatterProfileCard";
import { EmptyState, Loading } from "@/components/common/Loading";
import { GeoRadiusFilter, isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { useReferences } from "@/hooks/useReferences";
import { useLocalMode } from "@/context/LocalModeContext";

const POST_MAX = 3000;
const PLAIN = (html) =>
  (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<").replace(/&gt;/gi, ">").trim().length;

const BLANK_FILTERS = { connections: false, postTypes: [], industries: [], geoOn: false, center: null, radius: 50 };

const countActiveFilters = (f) =>
  (f.connections ? 1 : 0) + f.postTypes.length + f.industries.length + (f.geoOn && f.center ? 1 : 0);

/** Shared composer form (fields + actions). Reused by the desktop inline composer and the
 *  mobile bottom-sheet composer. `onDone` collapses/closes its host after post or cancel. */
function ComposerForm({ identities, labelOpts, onPosted, onDone }) {
  const [identityKey, setIdentityKey] = useState("");
  const [body, setBody] = useState("");
  const [media, setMedia] = useState(EMPTY_MEDIA);
  const [postTypeIds, setPostTypeIds] = useState([]);
  const [industryIds, setIndustryIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [assistFlow, setAssistFlow] = useState(null);

  useEffect(() => {
    if (!identityKey && identities.length) {
      setIdentityKey(`${identities[0].identity_type}:${identities[0].identity_id}`);
    }
  }, [identities, identityKey]);

  const [identityType, identityId] = identityKey ? identityKey.split(":") : ["personal", ""];
  // ONE shared Otter Assist state object — identical logic on every composer surface.
  const assist = useOtterAssist({
    identityType, identityId, postTypeIds,
    postDraft: useMemo(() => ({ body, media, post_type_ids: postTypeIds, industry_ids: industryIds }),
      [body, media, postTypeIds, industryIds]),
    onRestoreDraft: useCallback((d) => {
      if (d.body) setBody(d.body);
      if (d.media) setMedia(d.media);
      if (d.post_type_ids) setPostTypeIds(d.post_type_ids);
      if (d.industry_ids) setIndustryIds(d.industry_ids);
    }, []),
  });

  const emptyBody = !body || body === "<p></p>";
  const link = useLinkPreview(body, { enabled: !hasMedia(media) });
  const over = PLAIN(body) > POST_MAX;
  const canPost = (!emptyBody || hasMedia(media)) && !over && !!identityKey;

  const submit = async () => {
    if (!canPost) return;
    const [type, id] = identityKey.split(":");
    setBusy(true);
    try {
      const payload = {
        body: emptyBody ? "" : body, media: hasMedia(media) ? media : null,
        post_type_ids: postTypeIds, industry_ids: industryIds,
        link_preview_url: link.previewUrl,
      };
      // Otter Assist (OAD-051): ONE request. Absent key => untouched Chatter payload.
      if (assist.payload) payload.otter_assist = assist.payload;
      const setupSnapshot = assist.setup;
      const { data } = await api.post(`/chatter/${type}/${id}`, payload);
      onPosted(data);
      assist.reset();
      link.reset();
      try { localStorage.setItem(LAST_IDENTITY_KEY, identityKey); } catch (e) { /* ignore */ }
      if (data?.otter_assist?.attached) {
        setAssistFlow({ info: data.otter_assist, setup: setupSnapshot });
        toast.success("Update posted — Otter Assist is finding matches");
      } else {
        if (data?.otter_assist && !data.otter_assist.attached) {
          toast.error("Your update was posted, but Otter Assist couldn't start. Please try again.");
        } else {
          toast.success("Update posted");
        }
        onDone && onDone();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="chatter-composer-form">
      {identities.length > 1 && (
        <div className="mb-3">
          <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Posting as</label>
          <Select value={identityKey} onValueChange={setIdentityKey}>
            <SelectTrigger data-testid="chatter-identity-select"><SelectValue placeholder="Choose an identity" /></SelectTrigger>
            <SelectContent>
              {identities.map((i) => (
                <SelectItem key={`${i.identity_type}:${i.identity_id}`} value={`${i.identity_type}:${i.identity_id}`} data-testid={`chatter-identity-option-${i.identity_id}`}>
                  {i.display_name} · <span className="capitalize">{i.identity_type}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <RichTextEditor value={body} onChange={setBody} placeholder="Share an update with the community…" testId="chatter-global-input" mentions emoji
                      onSubmit={() => { if (!busy && canPost) submit(); }} />
      {!hasMedia(media) && (
        <>
          {link.loading && !link.preview && (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="link-preview-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> Fetching link preview…
            </p>
          )}
          <LinkPreviewCard preview={link.preview} onRemove={link.remove} testId="composer-link-preview" />
        </>
      )}
      <ChatterComposerMedia value={media} onChange={setMedia} />
      <LabelSelectors
        postTypeOpts={labelOpts.postTypes} industryOpts={labelOpts.industries}
        postTypeIds={postTypeIds} setPostTypeIds={setPostTypeIds}
        industryIds={industryIds} setIndustryIds={setIndustryIds} idSuffix="-global"
      />
      <div className="mt-3">
        <OtterAssistComposerPanel assist={assist} body={body}
          industryOpts={labelOpts.industries} idSuffix="-global" />
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <span className={`text-xs tabular-nums ${over ? "font-[600] text-destructive" : "text-muted-foreground"}`} data-testid="chatter-global-count">{PLAIN(body).toLocaleString()}/3,000</span>
        <div className="flex items-center gap-2">
          {/* Same shortcut, same words, same place as every other composer. */}
          <span className="hidden text-[11px] text-muted-foreground sm:inline"
                data-testid="composer-shortcut-hint">Ctrl+Enter to post</span>
          <Button variant="ghost" onClick={onDone} disabled={busy} data-testid="chatter-composer-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !canPost} data-testid="chatter-global-post-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {assist.payload ? "Post with Otter Assist" : "Post update"}
          </Button>
        </div>
      </div>
      {assistFlow && (
        <OtterAssistActivationDialog
          info={assistFlow.info} setup={assistFlow.setup} config={assist.config}
          industryOpts={labelOpts.industries} identityType={identityType}
          onClose={() => { setAssistFlow(null); onDone && onDone(); }}
        />
      )}
    </div>
  );
}

/** Desktop composer: a condensed pill that expands into the shared ComposerForm inline. */
function GlobalComposer({ identities, labelOpts, onPosted, expanded, setExpanded }) {
  if (!expanded) {
    return (
      <Card className="border-0 p-4" data-testid="chatter-global-composer">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setExpanded(true)}
            className="flex-1 rounded-full border border-border bg-secondary/50 px-4 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary"
            data-testid="chatter-composer-expand"
          >
            Share an update with the community…
          </button>
          <Button onClick={() => setExpanded(true)} data-testid="chatter-composer-open-button">
            <PenSquare className="mr-1.5 h-4 w-4" /> Post
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-0 p-5" data-testid="chatter-global-composer">
      <ComposerForm identities={identities} labelOpts={labelOpts} onPosted={onPosted} onDone={() => setExpanded(false)} />
    </Card>
  );
}

/** The filter controls themselves. `layout="row"` for the desktop bar, `layout="stack"` for
 *  the mobile bottom sheet. testId prefix differs so the two never collide in the DOM. */
function FilterControls({ industryOpts, postTypeOpts, filters, setFilters, layout = "row", tid = "chatter-filter" }) {
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const stacked = layout === "stack";

  const connectionsBtn = (
    <Button
      variant={filters.connections ? "default" : "secondary"} size="sm"
      onClick={() => set({ connections: !filters.connections })}
      className={stacked ? "h-10 w-full justify-center" : "h-10"} data-testid={`${tid}-connections`}
    >
      <Users className="mr-1.5 h-4 w-4" /> Connections{stacked ? " only" : ""}
    </Button>
  );

  if (stacked) {
    return (
      <div className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Show</label>
          {connectionsBtn}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Post type</label>
          <MultiSelect options={postTypeOpts} value={filters.postTypes} onChange={(v) => set({ postTypes: v })} placeholder="Any post type" testId={`${tid}-post-type`} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Industry</label>
          <MultiSelect options={industryOpts} value={filters.industries} onChange={(v) => set({ industries: v })} placeholder="Any industry" testId={`${tid}-industry`} />
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-[560] text-muted-foreground">Location</label>
          <GeoRadiusFilter value={{ geoOn: filters.geoOn, center: filters.center, radius: filters.radius }} onChange={(patch) => set(patch)} testId={`${tid}-geo`} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-start gap-2">
      {connectionsBtn}
      <div className="min-w-[150px] flex-1 sm:max-w-[190px]">
        <MultiSelect options={postTypeOpts} value={filters.postTypes} onChange={(v) => set({ postTypes: v })} placeholder="Post type" testId={`${tid}-post-type`} />
      </div>
      <div className="min-w-[150px] flex-1 sm:max-w-[190px]">
        <MultiSelect options={industryOpts} value={filters.industries} onChange={(v) => set({ industries: v })} placeholder="Industry" testId={`${tid}-industry`} />
      </div>
      <GeoRadiusFilter value={{ geoOn: filters.geoOn, center: filters.center, radius: filters.radius }} onChange={(patch) => set(patch)} testId={`${tid}-geo`} />
    </div>
  );
}

/** Desktop horizontal filter bar (unchanged behaviour). */
function FilterBar({ industryOpts, postTypeOpts, filters, setFilters }) {
  const clear = () => setFilters({ ...BLANK_FILTERS });
  const active = countActiveFilters(filters) > 0;

  return (
    <Card className="border-0 p-4" data-testid="chatter-filters">
      <div className="flex flex-wrap items-start gap-2">
        <FilterControls industryOpts={industryOpts} postTypeOpts={postTypeOpts} filters={filters} setFilters={setFilters} layout="row" tid="chatter-filter" />
        {active && (
          <Button variant="ghost" size="sm" onClick={clear} className="h-10" data-testid="chatter-filter-clear"><X className="mr-1.5 h-4 w-4" /> Clear</Button>
        )}
      </div>
    </Card>
  );
}

/** Mobile: two compact buttons (Filters + Post) that each open a bottom sheet. */
function MobileControls({ identities, labelOpts, industryOpts, postTypeOpts, filters, setFilters, onPosted }) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const count = countActiveFilters(filters);
  const clear = () => setFilters({ ...BLANK_FILTERS });

  return (
    <div className="mt-2 flex items-center gap-2">
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetTrigger asChild>
          <Button variant="secondary" className="h-11 flex-1 justify-center" data-testid="chatter-mobile-filters-button">
            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
            {count > 0 && (
              <span className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-[640] text-primary-foreground" data-testid="chatter-mobile-filters-count">{count}</span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl" data-testid="chatter-mobile-filters-sheet">
          <SheetHeader className="text-left"><SheetTitle>Filters</SheetTitle></SheetHeader>
          <div className="mt-4">
            <FilterControls industryOpts={industryOpts} postTypeOpts={postTypeOpts} filters={filters} setFilters={setFilters} layout="stack" tid="chatter-mfilter" />
          </div>
          <div className="mt-6 flex gap-2">
            <Button variant="ghost" className="flex-1" onClick={clear} disabled={count === 0} data-testid="chatter-mobile-filters-clear">Clear all</Button>
            <Button className="flex-1" onClick={() => setFiltersOpen(false)} data-testid="chatter-mobile-filters-apply">Show results</Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={postOpen} onOpenChange={setPostOpen}>
        <SheetTrigger asChild>
          <Button className="h-11 flex-1 justify-center" data-testid="chatter-mobile-post-button">
            <PenSquare className="mr-1.5 h-4 w-4" /> Post
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-2xl" data-testid="chatter-mobile-post-sheet">
          <SheetHeader className="text-left"><SheetTitle>Share an update</SheetTitle></SheetHeader>
          <div className="mt-4">
            <ComposerForm identities={identities} labelOpts={labelOpts} onPosted={onPosted} onDone={() => setPostOpen(false)} />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export function GlobalChatterFeed({ profile, identities, labelOpts }) {
  const { references } = useReferences();
  const localMode = useLocalMode();
  const [filters, setFilters] = useState({ ...BLANK_FILTERS });
  const [posts, setPosts] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [error, setError] = useState(false);
  const [rechatVisible, setRechatVisible] = useState(false);
  const industryOpts = (references.industries || []).map((i) => ({ value: i.value, label: i.label }));

  useEffect(() => {
    let active = true;
    api.get("/chatter/rechat/config")
      .then(({ data }) => { if (active) setRechatVisible(!!(data && data.visible)); })
      .catch(() => { /* Re-Chat optional */ });
    return () => { active = false; };
  }, []);

  const defaultIdentityKey = useMemo(() => {
    if (!identities || !identities.length) return null;
    let last = null;
    try { last = localStorage.getItem(LAST_IDENTITY_KEY); } catch (e) { /* ignore */ }
    if (last && identities.some((i) => identityKeyOf(i) === last)) return last;
    const personal = identities.find((i) => i.identity_type === "personal");
    return identityKeyOf(personal || identities[0]);
  }, [identities]);

  const buildParams = (p) => {
    const params = { page: p, limit: 20 };
    if (filters.connections) params.connections_only = true;
    if (filters.postTypes.length) params.post_type_id = filters.postTypes.join(",");
    if (filters.industries.length) params.industry = filters.industries.join(",");
    if (filters.geoOn && filters.center) {
      const c = filters.center;
      if (isRegionCenter(c)) {
        if (c.granularity === "state") params.state = c.state_code;
        else params.country = c.country_code;
      } else {
        params.lat = c.lat; params.lng = c.lng; params.radius = filters.radius;
      }
    }
    if (localMode.localParams) Object.assign(params, localMode.localParams);
    return params;
  };

  const centerKey = filters.center ? `${filters.center.lat},${filters.center.lng},${filters.center.granularity || ""}` : "";
  useEffect(() => {
    let active = true;
    setPosts(null); setError(false);
    (async () => {
      try {
        const { data } = await api.get("/chatter/feed", { params: buildParams(1) });
        if (!active) return;
        setPosts(data.posts || []); setHasMore(!!data.has_more); setPage(1);
      } catch (e) { if (active) { setError(true); setPosts([]); } }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.connections, filters.postTypes.join(","), filters.industries.join(","), filters.geoOn, filters.radius, centerKey, localMode.localKey]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/chatter/feed", { params: buildParams(page + 1) });
      setPosts((prev) => [...(prev || []), ...(data.posts || [])]);
      setHasMore(!!data.has_more);
      setPage((pp) => pp + 1);
    } catch (e) { toast.error("Could not load more posts"); }
    finally { setLoadingMore(false); }
  };

  const onPosted = (post) => setPosts((prev) => [post, ...(prev || [])]);
  const onDeleted = (id) => setPosts((prev) => (prev || []).filter((p) => p.id !== id));

  const anyFilter = countActiveFilters(filters) > 0;

  return (
    <div data-testid="chatter-global-feed">
      {/* Mobile: sticky slim profile card + condensed Filters / Post controls */}
      {/* Sits below the sticky nav strip (header 64px + strip 69px), not behind it. */}
      <div className="sticky top-[133px] z-20 bg-background pb-2 lg:hidden" data-testid="chatter-mobile-header">
        <ChatterProfileCard profile={profile} variant="slim" />
        <MobileControls
          identities={identities} labelOpts={labelOpts}
          industryOpts={industryOpts} postTypeOpts={labelOpts.postTypes}
          filters={filters} setFilters={setFilters} onPosted={onPosted}
        />
      </div>

      {/* Desktop: sticky filter bar + composer. The block stops being sticky while the composer is
          open, because an expanded composer is taller than the viewport and a sticky element that
          tall can never be scrolled into view — the feed behind it would scroll instead. */}
      <div className={`hidden lg:block lg:space-y-4 lg:bg-background lg:pb-4 ${
        composerOpen ? "" : "lg:sticky lg:top-20 lg:z-20"}`}>
        <FilterBar industryOpts={industryOpts} postTypeOpts={labelOpts.postTypes} filters={filters} setFilters={setFilters} />
        <GlobalComposer identities={identities} labelOpts={labelOpts} onPosted={onPosted}
          expanded={composerOpen} setExpanded={setComposerOpen} />
      </div>

      <div className="mt-2 space-y-3 lg:mt-0 lg:space-y-4">
        {posts === null ? (
          <Card className="border-0 p-8"><Loading /></Card>
        ) : error ? (
          <Card className="border-0 p-8">
            <EmptyState icon={AlertCircle} title="Couldn't load Chatter" description="Something went wrong loading the feed. Please try again." testId="chatter-feed-error" />
          </Card>
        ) : posts.length === 0 ? (
          <Card className="border-0 p-8">
            <EmptyState
              icon={MessageCircle}
              title={anyFilter ? "No posts match these filters yet." : "Chatter is quiet right now."}
              description={anyFilter ? "Try clearing or changing your filters." : "Be the first to start the conversation."}
              testId="chatter-feed-empty"
            />
          </Card>
        ) : (
          <Card className="border-0 px-5 py-2 sm:px-6">
            <div className="divide-y divide-border">
              {posts.map((p) => (
                <PostCard key={p.id} post={p} onDeleted={onDeleted} postTypeOpts={labelOpts.postTypes} industryOpts={labelOpts.industries} identities={identities} rechatVisible={rechatVisible} defaultIdentityKey={defaultIdentityKey} onRechatPosted={onPosted} />
              ))}
            </div>
            {hasMore && (
              <div className="py-4 flex justify-center">
                <Button variant="secondary" onClick={loadMore} disabled={loadingMore} data-testid="chatter-feed-load-more">
                  {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Load more
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}

export default GlobalChatterFeed;
